/*
 * EINGEFROREN durch AP-1 — Änderungen nur per Änderungsantrag in PROGRESS.md.
 * Gegenstück zu CAN_MON_Cpp_Win11_Native_B_Style/src/can/{CanFrame,ICanAdapter}.h
 * Beschreibung siehe CONTRACTS.md §1/§2.
 */

/** Entspricht `struct CanFrame` (CanFrame.h). `data` ist IMMER 8 Bytes lang. */
export interface CanFrame {
  /** 11 Bit (<= 0x7FF) oder 29 Bit (<= 0x1FFFFFFF), ohne Flag-Bits. */
  id: number;
  /** 0..8 — in JEDEM Adapter klemmen, siehe CONTRACTS.md §2.3. */
  dlc: number;
  data: Uint8Array;
  extended: boolean;
  remote: boolean;
  /** Sekunden, monotone Zeitbasis des Erzeugers (`CanFrame::timestamp_s`). */
  timestamp_s: number;
}

/** Eingabeform für `send()`: `data` darf kürzer als 8 Byte sein, Rest = 0. */
export interface CanFrameInit {
  id: number;
  data: Uint8Array | readonly number[];
  extended?: boolean;
  remote?: boolean;
  /** Default: Länge von `data`, auf 0..8 geklemmt. */
  dlc?: number;
}

/** Entspricht `struct CanBusStatus`. Nicht verfügbare Felder werden als "N/A" angezeigt. */
export interface CanBusStatus {
  stateAvailable: boolean;
  /** "Active" | "Warning" | "Passive" | "Bus Off" | "" */
  state: string;
  countersAvailable: boolean;
  rxErrorCount: number;
  txErrorCount: number;
}

export const emptyBusStatus: CanBusStatus = {
  stateAvailable: false,
  state: '',
  countersAvailable: false,
  rxErrorCount: 0,
  txErrorCount: 0,
};

/** Die neun vom SLCAN-Kommando `S<code>` unterstützten Bitraten in bit/s. */
export const CAN_BITRATES = [
  10_000, 20_000, 50_000, 100_000, 125_000, 250_000, 500_000, 800_000, 1_000_000,
] as const;
export type CanBitrate = (typeof CAN_BITRATES)[number];

/** `BitrateToSlcanCode()` aus CanableAdapter.cpp, 1:1. */
export const SLCAN_BITRATE_CODE: Readonly<Record<CanBitrate, string>> = {
  10_000: '0',
  20_000: '1',
  50_000: '2',
  100_000: '3',
  125_000: '4',
  250_000: '5',
  500_000: '6',
  800_000: '7',
  1_000_000: '8',
};

/** Reihenfolge des Baudrate-Combos im Original (kbit/s, absteigend). Default: 250. */
export const BITRATE_COMBO_KBPS = [1000, 800, 500, 250, 125, 100, 50, 25] as const;
export const DEFAULT_BITRATE: CanBitrate = 250_000;

/** Data-Bitrate-Combo (nur für FD-fähige Adapter sichtbar), kbit/s. Default: 2000. */
export const DATA_BITRATE_COMBO_KBPS = [8000, 4000, 2000, 1000, 500] as const;
export const DEFAULT_DATA_BITRATE = 2_000_000;

/** USB-IDs aus `ScorePort()` — Filter für `navigator.serial.requestPort()`. */
export const KNOWN_USB_FILTERS = [
  { usbVendorId: 0xad50, usbProductId: 0x60c4 }, // CANable (Original)
  { usbVendorId: 0x1d50, usbProductId: 0x606f }, // candleLight / CANable gs_usb
  { usbVendorId: 0x1209, usbProductId: 0x2323 }, // CANtact
] as const;

/**
 * Verweis auf einen bereits vom Nutzer freigegebenen Port. `requestPort()`
 * braucht eine Nutzergeste und läuft daher im Main-Thread; der Worker löst
 * die Referenz über `navigator.serial.getPorts()` auf. Siehe CONTRACTS.md §2.2.
 */
export interface PortRef {
  /** Index in `navigator.serial.getPorts()` zum Zeitpunkt der Auflösung. */
  index: number;
  usbVendorId?: number;
  usbProductId?: number;
  /** Anzeigetext für Statusleiste/Combo, z. B. "CANable (AD50:60C4)". */
  label: string;
}

export interface OpenOptions {
  port: PortRef;
  bitrate: CanBitrate;
  /** Nur ausgewertet, wenn `supportsCanFd` true ist. */
  dataBitrate?: number;
  /** 0-basiert; nur relevant wenn `channelCount > 1`. */
  channelIndex?: number;
}

export type CanErrorCode =
  | 'no-web-serial'
  | 'no-port'
  | 'port-busy'
  | 'handshake-failed'
  | 'unsupported-bitrate'
  | 'write-failed'
  | 'read-failed'
  | 'device-lost'
  | 'not-open';

export class CanAdapterError extends Error {
  constructor(
    readonly code: CanErrorCode,
    message: string,
    options?: { cause?: unknown },
  ) {
    super(message, options);
    this.name = 'CanAdapterError';
  }
}

/**
 * Web-Pendant zu `class ICanAdapter`. Unterschiede zum C++-Original:
 * - `Open/Close/Send` sind asynchron (Streams statt blockierender Handles).
 * - `RecvNoWait`/`WaitForReceive` entfallen; Empfang läuft über `frames`.
 * - `ScanCandidates()` liefert nur bereits freigegebene Ports (kein SetupAPI).
 */
export interface ICanAdapter {
  /** `Name()` — Anzeigename im Interface-Combo, z. B. "CANable (SLCAN)". */
  readonly name: string;
  readonly isOpen: boolean;
  /** `SupportsCanFd()` — Default false. */
  readonly supportsCanFd: boolean;
  /** `ChannelCount()` — Default 1. */
  readonly channelCount: number;

  /** `ScanCandidates()`. */
  scanCandidates(): Promise<PortRef[]>;
  /** Wirft `CanAdapterError`. Nach Rückkehr ist `frames` gültig. */
  open(options: OpenOptions): Promise<void>;
  /** Idempotent; bricht einen laufenden `frames`-Reader ab. */
  close(): Promise<void>;
  /** Wirft `CanAdapterError` bei Schreibfehlern. */
  send(frame: CanFrameInit): Promise<void>;
  /**
   * Gültig nur zwischen `open()` und `close()`; wird von `close()` beendet.
   * Genau EIN Reader (der can-core-Worker) darf den Stream konsumieren.
   */
  readonly frames: ReadableStream<CanFrame>;
  /** `GetBusStatus()` — Default: alle Verfügbarkeitsflags false. */
  getBusStatus(): Promise<CanBusStatus>;
}
